import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, TextField, Button, Paper, Alert, CircularProgress,
  Fade, Chip, Divider, Stepper, Step, StepLabel, StepConnector, Pagination,
  InputAdornment, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip
} from '@mui/material';
import {
  Globe, CheckCircle2, XCircle, Copy, Server, Mail, ArrowRight,
  ArrowLeft, RefreshCw, Search, ExternalLink, Check, MailCheck, Inbox, Clock
} from 'lucide-react';
import { styled } from '@mui/material/styles';
import { stepConnectorClasses } from '@mui/material/StepConnector';
import axios from 'axios';
import { getDisplayDomain, getNomeRegistroDns, getDominioRaiz } from '../utils/domainUtils';

const SectionCard = styled(Paper)(() => ({
  background: '#141926',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 16,
  padding: 24,
  marginBottom: 24,
}));

const InstructionBox = styled(Box)(() => ({
  background: 'rgba(124,58,237,0.06)',
  border: '1px solid rgba(124,58,237,0.15)',
  borderRadius: 12,
  padding: 20,
}));

const FtmConnector = styled(StepConnector)(() => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: { top: 18 },
  [`&.${stepConnectorClasses.active}`]: { [`& .${stepConnectorClasses.line}`]: { borderColor: '#7C3AED' } },
  [`&.${stepConnectorClasses.completed}`]: { [`& .${stepConnectorClasses.line}`]: { borderColor: '#10B981' } },
  [`& .${stepConnectorClasses.line}`]: { borderColor: 'rgba(107,114,128,0.3)', borderTopWidth: 2 },
}));

const FtmStepIcon = ({ active, completed, icon }) => (
  <Box sx={{
    width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '0.85rem', fontWeight: 700,
    background: completed ? '#10B981' : active ? '#7C3AED' : 'rgba(107,114,128,0.2)',
    color: '#fff', transition: 'all 0.3s',
  }}>
    {completed ? <Check size={16} /> : icon}
  </Box>
);

const SERVER_IP = '187.127.21.250';
const SITES_PER_PAGE = 5;

export default function DominioProprio() {
  const [activeStep, setActiveStep] = useState(0);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [domain, setDomain] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);

  const [dnsStatus, setDnsStatus] = useState(null);
  const [dnsChecking, setDnsChecking] = useState(false);
  const [activating, setActivating] = useState(false);

  const [mxStatus, setMxStatus] = useState(null);
  const [mxChecking, setMxChecking] = useState(false);
  const [settingUpEmail, setSettingUpEmail] = useState(false);

  const [emails, setEmails] = useState([]);
  const [loadingEmails, setLoadingEmails] = useState(false);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copied, setCopied] = useState('');

  const fetchSites = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/sites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) setSites([...(res.data.sites || [])].reverse());
    } catch { setError('Erro ao carregar sites'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSites(); }, [fetchSites]);

  const selectedSite = sites.find(s => s._id === selectedSiteId);

  const dominioAtual = selectedSite?.customDomain || domain;
  const nomeRegistro = getNomeRegistroDns(dominioAtual);
  const ehSubdominio = nomeRegistro !== '@';

  const filteredSites = useMemo(() => {
    if (!searchTerm.trim()) return sites;
    const term = searchTerm.toLowerCase();
    return sites.filter(s => {
      const name = (s.name || s.config?.DOMINIO || '').toLowerCase();
      const cd = (s.customDomain || '').toLowerCase();
      return name.includes(term) || cd.includes(term);
    });
  }, [sites, searchTerm]);

  const totalPages = Math.ceil(filteredSites.length / SITES_PER_PAGE);
  const paginatedSites = useMemo(() => {
    const start = (page - 1) * SITES_PER_PAGE;
    return filteredSites.slice(start, start + SITES_PER_PAGE);
  }, [filteredSites, page]);

  const domainRegex = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*\.[a-z]{2,}$/;
  const isDomainValid = domain.trim() && domainRegex.test(domain.trim());

  const handleCopy = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const handleCheckDns = async () => {
    if (!domain.trim()) { setError('Informe o domínio'); return; }
    setError(''); setDnsChecking(true); setDnsStatus(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/sites/${selectedSiteId}/check-dns`,
        { params: { domain: domain.trim() }, headers: { Authorization: `Bearer ${token}` } }
      );
      setDnsStatus(res.data);
    } catch { setError('Erro ao verificar DNS'); }
    finally { setDnsChecking(false); }
  };

  const handleActivateDomain = async () => {
    setError(''); setSuccess(''); setActivating(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/sites/${selectedSiteId}/custom-domain`,
        { domain: domain.trim() },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 180000 }
      );
      if (res.data.success) {
        setSuccess(`Domínio ${domain.trim()} ativado com SSL!`);
        await fetchSites();
        setActiveStep(2);
      } else { setError(res.data.message); }
    } catch (err) { setError(err.response?.data?.message || 'Erro ao ativar domínio'); }
    finally { setActivating(false); }
  };

  const handleCheckMx = async () => {
    setError(''); setMxChecking(true); setMxStatus(null);
    const siteId = selectedSiteId;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/sites/${siteId}/check-mx`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMxStatus(res.data);
    } catch { setError('Erro ao verificar MX'); }
    finally { setMxChecking(false); }
  };

  const handleSetupEmail = async () => {
    setError(''); setSuccess(''); setSettingUpEmail(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/sites/${selectedSiteId}/setup-email`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        setSuccess('Email ativado com sucesso!');
        await fetchSites();
        setActiveStep(3);
        fetchEmails();
      } else { setError(res.data.message); }
    } catch (err) { setError(err.response?.data?.message || 'Erro ao configurar email'); }
    finally { setSettingUpEmail(false); }
  };

  const fetchEmails = async () => {
    setLoadingEmails(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/emails`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        const siteDomain = selectedSite?.customDomain;
        if (siteDomain) {
          setEmails(res.data.emails.filter(e =>
            e.recipient && e.recipient.endsWith(`@${siteDomain}`)
          ).slice(0, 10));
        }
      }
    } catch { /* ignore */ }
    finally { setLoadingEmails(false); }
  };

  const steps = ['Selecionar Site', 'Apontar DNS', 'Configurar Email', 'Caixa de Entrada'];

  const siteHasCustomDomain = selectedSite?.customDomain;
  const siteHasEmail = selectedSite?.emailEnabled;

  useEffect(() => {
    if (selectedSite) {
      if (selectedSite.customDomain) {
        setDomain(selectedSite.customDomain);
        if (selectedSite.emailEnabled) {
          setActiveStep(3);
          fetchEmails();
        } else {
          setActiveStep(2);
        }
      } else {
        setDomain('');
        setActiveStep(0);
      }
    } else {
      setDomain('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSiteId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}>
        <CircularProgress size={32} sx={{ color: '#7C3AED' }} />
      </Box>
    );
  }

  return (
    <Fade in timeout={400}>
      <Box sx={{ maxWidth: 840, mx: 'auto' }}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <Globe size={22} color="#7C3AED" />
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#F9FAFB', letterSpacing: '-0.02em' }}>
              Domínio Próprio + Email
            </Typography>
            <Chip label="BETA" size="small" sx={{ background: 'rgba(245,158,11,0.15)', color: '#F59E0B', fontWeight: 700, fontSize: '0.7rem', height: 22 }} />
          </Box>
          <Typography variant="body2" sx={{ color: '#6B7280' }}>
            Configure domínio personalizado e receba emails para verificação Meta
          </Typography>
        </Box>

        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2, background: 'rgba(239,68,68,0.1)', color: '#FCA5A5', border: '1px solid rgba(239,68,68,0.2)', '& .MuiAlert-icon': { color: '#EF4444' } }}>{error}</Alert>}
        {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2, background: 'rgba(16,185,129,0.1)', color: '#6EE7B7', border: '1px solid rgba(16,185,129,0.2)', '& .MuiAlert-icon': { color: '#10B981' } }}>{success}</Alert>}

        <Box sx={{ mb: 4 }}>
          <Stepper activeStep={activeStep} alternativeLabel connector={<FtmConnector />}>
            {steps.map((label, i) => (
              <Step key={label} completed={i < activeStep}>
                <StepLabel StepIconComponent={(props) => <FtmStepIcon {...props} icon={i + 1} />}>
                  <Typography sx={{ color: i <= activeStep ? '#D1D5DB' : '#4B5563', fontSize: '0.78rem', fontWeight: i === activeStep ? 600 : 400 }}>
                    {label}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {activeStep === 0 && (
          <SectionCard elevation={0}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#F9FAFB', mb: 0.5, fontSize: '1rem' }}>
              Etapa 1: Selecionar Site
            </Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', mb: 3 }}>
              Escolha o site e informe o domínio que deseja configurar
            </Typography>

            {sites.length > 5 && (
              <TextField
                size="small" placeholder="Buscar site..." fullWidth
                value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setPage(1); }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Search size={16} color="#6B7280" /></InputAdornment>,
                }}
                sx={{ mb: 2, '& .MuiOutlinedInput-root': { background: 'rgba(255,255,255,0.03)', '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' }, '&:hover fieldset': { borderColor: 'rgba(124,58,237,0.3)' }, '&.Mui-focused fieldset': { borderColor: '#7C3AED' } }, '& .MuiInputBase-input': { color: '#D1D5DB', fontSize: '0.85rem' } }}
              />
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
              {paginatedSites.map(site => {
                const siteName = site.name || site.config?.DOMINIO || 'site';
                const displayDomain = getDisplayDomain(site);
                const isSelected = selectedSiteId === site._id;
                return (
                  <Box
                    key={site._id}
                    onClick={() => { setSelectedSiteId(site._id); setError(''); setSuccess(''); setDnsStatus(null); setMxStatus(null); }}
                    sx={{
                      p: 2, borderRadius: 2, cursor: 'pointer', transition: 'all 0.2s',
                      border: isSelected ? '1px solid #7C3AED' : '1px solid rgba(255,255,255,0.06)',
                      background: isSelected ? 'rgba(124,58,237,0.08)' : 'rgba(255,255,255,0.02)',
                      '&:hover': { background: isSelected ? 'rgba(124,58,237,0.12)' : 'rgba(255,255,255,0.04)' },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography sx={{ fontWeight: 600, color: '#F9FAFB', fontSize: '0.88rem' }}>{siteName}</Typography>
                        <Typography sx={{ color: '#6B7280', fontSize: '0.78rem' }}>{displayDomain}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {site.customDomain && <Chip size="small" icon={<Globe size={12} />} label={site.customDomain} sx={{ background: 'rgba(16,185,129,0.1)', color: '#10B981', fontSize: '0.72rem', '& .MuiChip-icon': { color: '#10B981' } }} />}
                        {site.emailEnabled && <Chip size="small" icon={<Mail size={12} />} label="Email" sx={{ background: 'rgba(124,58,237,0.1)', color: '#A78BFA', fontSize: '0.72rem', '& .MuiChip-icon': { color: '#A78BFA' } }} />}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} size="small"
                  sx={{ '& .MuiPaginationItem-root': { color: '#9CA3AF' }, '& .Mui-selected': { background: 'rgba(124,58,237,0.2) !important', color: '#A78BFA' } }} />
              </Box>
            )}

            {selectedSiteId && !siteHasCustomDomain && (
              <>
                <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', my: 2 }} />
                <Typography variant="body2" sx={{ color: '#D1D5DB', mb: 1.5, fontWeight: 600 }}>Domínio personalizado</Typography>
                <TextField
                  size="small" fullWidth placeholder="ex: seudominio.com.br"
                  value={domain} onChange={e => setDomain(e.target.value.toLowerCase().replace(/\s/g, ''))}
                  error={domain.trim().length > 0 && !isDomainValid}
                  helperText={domain.trim().length > 0 && !isDomainValid ? 'Formato inválido. Ex: seudominio.com.br' : ''}
                  sx={{ '& .MuiOutlinedInput-root': { background: 'rgba(255,255,255,0.03)', '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' }, '&:hover fieldset': { borderColor: 'rgba(124,58,237,0.3)' }, '&.Mui-focused fieldset': { borderColor: '#7C3AED' } }, '& .MuiInputBase-input': { color: '#D1D5DB', fontSize: '0.9rem' }, '& .MuiFormHelperText-root': { color: '#EF4444', fontSize: '0.75rem' } }}
                />
              </>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                variant="contained" endIcon={<ArrowRight size={16} />}
                disabled={!selectedSiteId || (!siteHasCustomDomain && !isDomainValid)}
                onClick={() => { setError(''); setSuccess(''); setActiveStep(siteHasCustomDomain ? (siteHasEmail ? 3 : 2) : 1); if (siteHasEmail) fetchEmails(); }}
                sx={{ background: '#7C3AED', textTransform: 'none', fontWeight: 600, '&:hover': { background: '#6D28D9' }, '&.Mui-disabled': { background: 'rgba(107,114,128,0.2)', color: '#4B5563' } }}
              >
                Próximo
              </Button>
            </Box>
          </SectionCard>
        )}

        {activeStep === 1 && (
          <SectionCard elevation={0}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#F9FAFB', mb: 0.5, fontSize: '1rem' }}>
              Etapa 2: Apontar DNS (Registro A)
            </Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', mb: 3 }}>
              Configure o registro A do seu domínio para apontar para nosso servidor
            </Typography>

            <InstructionBox>
              <Typography sx={{ color: '#D1D5DB', fontWeight: 600, mb: 2, fontSize: '0.88rem' }}>
                No painel DNS do seu domínio, crie/edite o registro:
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mb: 2 }}>
                <Box>
                  <Typography sx={{ color: '#6B7280', fontSize: '0.72rem', textTransform: 'uppercase', mb: 0.5 }}>Tipo</Typography>
                  <Chip label="A" size="small" sx={{ background: 'rgba(124,58,237,0.15)', color: '#A78BFA', fontWeight: 700 }} />
                </Box>
                <Box>
                  <Typography sx={{ color: '#6B7280', fontSize: '0.72rem', textTransform: 'uppercase', mb: 0.5 }}>Nome</Typography>
                  <Chip label={nomeRegistro} size="small" sx={{ background: 'rgba(124,58,237,0.15)', color: '#A78BFA', fontWeight: 700 }} />
                </Box>
                <Box>
                  <Typography sx={{ color: '#6B7280', fontSize: '0.72rem', textTransform: 'uppercase', mb: 0.5 }}>Valor</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label={SERVER_IP} size="small" sx={{ background: 'rgba(124,58,237,0.15)', color: '#A78BFA', fontWeight: 700 }} />
                    <Tooltip title={copied === 'ip' ? 'Copiado!' : 'Copiar IP'}>
                      <IconButton size="small" onClick={() => handleCopy(SERVER_IP, 'ip')}>
                        {copied === 'ip' ? <Check size={14} color="#10B981" /> : <Copy size={14} color="#6B7280" />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>

              <Alert severity="info" sx={{ background: 'rgba(59,130,246,0.08)', color: '#93C5FD', border: '1px solid rgba(59,130,246,0.15)', '& .MuiAlert-icon': { color: '#3B82F6' }, fontSize: '0.82rem', mb: 1 }}>
                Se usar Cloudflare, desative o proxy (nuvem laranja) para este registro e deixe apenas DNS (nuvem cinza).
              </Alert>
              <Alert severity="info" icon={<Clock size={18} />} sx={{ background: 'rgba(124,58,237,0.06)', color: '#C4B5FD', border: '1px solid rgba(124,58,237,0.15)', '& .MuiAlert-icon': { color: '#A78BFA' }, fontSize: '0.82rem' }}>
                Depois de criar o registro, a propagação do DNS pode levar de alguns minutos até algumas horas, dependendo do seu provedor. Se a verificação falhar logo após salvar, aguarde um pouco e tente de novo — é normal.
              </Alert>
            </InstructionBox>

            <Box sx={{ mt: 3 }}>
              <Typography sx={{ color: '#D1D5DB', fontWeight: 600, mb: 1, fontSize: '0.88rem' }}>
                Domínio: <span style={{ color: '#A78BFA' }}>{domain}</span>
              </Typography>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined" startIcon={dnsChecking ? <CircularProgress size={14} /> : <RefreshCw size={14} />}
                  disabled={dnsChecking} onClick={handleCheckDns}
                  sx={{ textTransform: 'none', borderColor: 'rgba(124,58,237,0.3)', color: '#A78BFA', '&:hover': { borderColor: '#7C3AED', background: 'rgba(124,58,237,0.08)' } }}
                >
                  Verificar DNS
                </Button>
              </Box>

              {dnsStatus && (
                <Box sx={{ mt: 2 }}>
                  {dnsStatus.warnings?.length > 0 && !dnsStatus.canActivate && (
                    <Alert severity="warning" icon={<XCircle size={18} />} sx={{ background: 'rgba(245,158,11,0.08)', color: '#FCD34D', border: '1px solid rgba(245,158,11,0.15)', '& .MuiAlert-icon': { color: '#F59E0B' }, mb: 2 }}>
                      {dnsStatus.warnings.map((w, i) => <Box key={i}>{w}</Box>)}
                      {dnsStatus.addresses?.length > 0 && (
                        <Box sx={{ mt: 1, fontSize: '0.82rem' }}>
                          Registros encontrados: {dnsStatus.addresses.join(', ')}
                        </Box>
                      )}
                    </Alert>
                  )}
                  {dnsStatus.canActivate && (
                    <Box>
                      <Alert severity="success" icon={<CheckCircle2 size={18} />} sx={{ background: 'rgba(16,185,129,0.08)', color: '#6EE7B7', border: '1px solid rgba(16,185,129,0.15)', '& .MuiAlert-icon': { color: '#10B981' }, mb: 2 }}>
                        DNS configurado corretamente! O registro A aponta para {SERVER_IP}
                      </Alert>
                      <Button
                        variant="contained" startIcon={activating ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <Server size={14} />}
                        disabled={activating} onClick={handleActivateDomain}
                        sx={{ background: '#10B981', textTransform: 'none', fontWeight: 600, '&:hover': { background: '#059669' } }}
                      >
                        {activating ? 'Ativando Nginx + SSL...' : 'Ativar Domínio + SSL'}
                      </Button>
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button startIcon={<ArrowLeft size={16} />} onClick={() => { setActiveStep(0); setDnsStatus(null); }}
                sx={{ textTransform: 'none', color: '#9CA3AF', '&:hover': { color: '#D1D5DB' } }}>Voltar</Button>
            </Box>
          </SectionCard>
        )}

        {activeStep === 2 && (
          <SectionCard elevation={0}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#F9FAFB', mb: 0.5, fontSize: '1rem' }}>
              Etapa 3: Configurar Email (Registro MX)
            </Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', mb: 3 }}>
              Configure o registro MX para receber emails no seu domínio
            </Typography>

            <InstructionBox>
              <Typography sx={{ color: '#D1D5DB', fontWeight: 600, mb: 2, fontSize: '0.88rem' }}>
                No painel DNS do seu domínio, crie o registro MX:
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 2, mb: 2 }}>
                <Box>
                  <Typography sx={{ color: '#6B7280', fontSize: '0.72rem', textTransform: 'uppercase', mb: 0.5 }}>Tipo</Typography>
                  <Chip label="MX" size="small" sx={{ background: 'rgba(124,58,237,0.15)', color: '#A78BFA', fontWeight: 700 }} />
                </Box>
                <Box>
                  <Typography sx={{ color: '#6B7280', fontSize: '0.72rem', textTransform: 'uppercase', mb: 0.5 }}>Nome</Typography>
                  <Chip label={nomeRegistro} size="small" sx={{ background: 'rgba(124,58,237,0.15)', color: '#A78BFA', fontWeight: 700 }} />
                </Box>
                <Box>
                  <Typography sx={{ color: '#6B7280', fontSize: '0.72rem', textTransform: 'uppercase', mb: 0.5 }}>Valor</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label="ftmpainel.com.br" size="small" sx={{ background: 'rgba(124,58,237,0.15)', color: '#A78BFA', fontWeight: 700 }} />
                    <Tooltip title={copied === 'mx' ? 'Copiado!' : 'Copiar'}>
                      <IconButton size="small" onClick={() => handleCopy('ftmpainel.com.br', 'mx')}>
                        {copied === 'mx' ? <Check size={14} color="#10B981" /> : <Copy size={14} color="#6B7280" />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                <Box>
                  <Typography sx={{ color: '#6B7280', fontSize: '0.72rem', textTransform: 'uppercase', mb: 0.5 }}>Prioridade</Typography>
                  <Chip label="10" size="small" sx={{ background: 'rgba(124,58,237,0.15)', color: '#A78BFA', fontWeight: 700 }} />
                </Box>
              </Box>

              {ehSubdominio && (
                <Alert severity="warning" sx={{ background: 'rgba(245,158,11,0.08)', color: '#FCD34D', border: '1px solid rgba(245,158,11,0.2)', '& .MuiAlert-icon': { color: '#F59E0B' }, fontSize: '0.82rem', mb: 1 }}>
                  Seu domínio é um subdomínio, então o campo <strong>Nome</strong> não é "@": use exatamente <strong>{nomeRegistro}</strong>, dentro da zona de <strong>{getDominioRaiz(dominioAtual)}</strong>.
                </Alert>
              )}
              <Alert severity="info" sx={{ background: 'rgba(59,130,246,0.08)', color: '#93C5FD', border: '1px solid rgba(59,130,246,0.15)', '& .MuiAlert-icon': { color: '#3B82F6' }, fontSize: '0.82rem', mb: 1 }}>
                Isso faz com que emails enviados para qualquer endereço @{selectedSite?.customDomain || domain} sejam recebidos pelo nosso servidor.
              </Alert>
              <Alert severity="info" sx={{ background: 'rgba(59,130,246,0.08)', color: '#93C5FD', border: '1px solid rgba(59,130,246,0.15)', '& .MuiAlert-icon': { color: '#3B82F6' }, fontSize: '0.82rem', mb: 1 }}>
                Tem vídeo aula explicando na página de <a href="/video-aulas" style={{ color: '#A78BFA', textDecoration: 'underline' }}>Vídeo Aulas</a>.
              </Alert>
              <Alert severity="info" icon={<Clock size={18} />} sx={{ background: 'rgba(124,58,237,0.06)', color: '#C4B5FD', border: '1px solid rgba(124,58,237,0.15)', '& .MuiAlert-icon': { color: '#A78BFA' }, fontSize: '0.82rem' }}>
                Registros MX costumam demorar um pouco mais para propagar que registros A. Se a verificação não passar logo de cara, espere alguns minutos e tente novamente — é normal e não é um erro.
              </Alert>
            </InstructionBox>

            <Box sx={{ mt: 3 }}>
              <Typography sx={{ color: '#D1D5DB', fontWeight: 600, mb: 1, fontSize: '0.88rem' }}>
                Domínio: <span style={{ color: '#A78BFA' }}>{selectedSite?.customDomain || domain}</span>
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button
                  variant="outlined" startIcon={mxChecking ? <CircularProgress size={14} /> : <RefreshCw size={14} />}
                  disabled={mxChecking} onClick={handleCheckMx}
                  sx={{ textTransform: 'none', borderColor: 'rgba(124,58,237,0.3)', color: '#A78BFA', '&:hover': { borderColor: '#7C3AED', background: 'rgba(124,58,237,0.08)' } }}
                >
                  Verificar MX
                </Button>
              </Box>

              {mxStatus && (
                <Box sx={{ mb: 2 }}>
                  {mxStatus.configured ? (
                    <Box>
                      <Alert severity="success" icon={<CheckCircle2 size={18} />} sx={{ background: 'rgba(16,185,129,0.08)', color: '#6EE7B7', border: '1px solid rgba(16,185,129,0.15)', '& .MuiAlert-icon': { color: '#10B981' }, mb: 2 }}>
                        MX configurado! Aponta para ftmpainel.com.br
                        {mxStatus.mxRecords?.length > 0 && (
                          <Box sx={{ mt: 0.5, fontSize: '0.82rem' }}>
                            Registros: {mxStatus.mxRecords.map(r => `${r.exchange} (prioridade ${r.priority})`).join(', ')}
                          </Box>
                        )}
                      </Alert>
                      <Button
                        variant="contained" startIcon={settingUpEmail ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <MailCheck size={14} />}
                        disabled={settingUpEmail} onClick={handleSetupEmail}
                        sx={{ background: '#10B981', textTransform: 'none', fontWeight: 600, '&:hover': { background: '#059669' } }}
                      >
                        {settingUpEmail ? 'Ativando email...' : 'Ativar Recebimento de Email'}
                      </Button>
                    </Box>
                  ) : (
                    <Alert severity="warning" icon={<XCircle size={18} />} sx={{ background: 'rgba(245,158,11,0.08)', color: '#FCD34D', border: '1px solid rgba(245,158,11,0.15)', '& .MuiAlert-icon': { color: '#F59E0B' } }}>
                      MX não configurado. O registro MX de {selectedSite?.customDomain || domain} não aponta para ftmpainel.com.br.
                      {mxStatus.mxRecords?.length > 0 ? (
                        <Box sx={{ mt: 0.5, fontSize: '0.82rem' }}>
                          Registros encontrados: {mxStatus.mxRecords.map(r => `${r.exchange} (prioridade ${r.priority})`).join(', ')}
                        </Box>
                      ) : (
                        <Box sx={{ mt: 0.5, fontSize: '0.82rem' }}>Nenhum registro MX encontrado.</Box>
                      )}
                    </Alert>
                  )}
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button startIcon={<ArrowLeft size={16} />} onClick={() => setActiveStep(siteHasCustomDomain ? 0 : 1)}
                sx={{ textTransform: 'none', color: '#9CA3AF', '&:hover': { color: '#D1D5DB' } }}>Voltar</Button>
            </Box>
          </SectionCard>
        )}

        {activeStep === 3 && (
          <SectionCard elevation={0}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#F9FAFB', mb: 0.5, fontSize: '1rem' }}>
              Etapa 4: Caixa de Entrada Pronta
            </Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', mb: 3 }}>
              Seu email está ativo e pronto para receber mensagens
            </Typography>

            <InstructionBox sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <MailCheck size={20} color="#10B981" />
                <Typography sx={{ color: '#10B981', fontWeight: 700, fontSize: '0.95rem' }}>
                  Email ativo!
                </Typography>
              </Box>

              <Typography sx={{ color: '#D1D5DB', mb: 2, fontSize: '0.88rem' }}>
                Qualquer email enviado para <strong style={{ color: '#A78BFA' }}>contato@{selectedSite?.customDomain}</strong> será recebido aqui.
              </Typography>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  icon={copied === 'email' ? <Check size={14} /> : <Copy size={14} />}
                  label={`contato@${selectedSite?.customDomain}`}
                  onClick={() => handleCopy(`contato@${selectedSite?.customDomain}`, 'email')}
                  sx={{
                    background: 'rgba(124,58,237,0.15)', color: '#A78BFA', cursor: 'pointer', fontWeight: 600,
                    '& .MuiChip-icon': { color: copied === 'email' ? '#10B981' : '#A78BFA' },
                    '&:hover': { background: 'rgba(124,58,237,0.25)' }
                  }}
                />
              </Box>

              <Alert severity="info" sx={{ mt: 2, background: 'rgba(59,130,246,0.08)', color: '#93C5FD', border: '1px solid rgba(59,130,246,0.15)', '& .MuiAlert-icon': { color: '#3B82F6' }, fontSize: '0.82rem' }}>
                Use este email na verificação do Meta Business. A Meta enviará o código para este endereço.
              </Alert>
            </InstructionBox>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography sx={{ color: '#D1D5DB', fontWeight: 600, fontSize: '0.9rem' }}>
                Últimos emails recebidos
              </Typography>
              <Button
                size="small" startIcon={loadingEmails ? <CircularProgress size={12} /> : <RefreshCw size={14} />}
                disabled={loadingEmails} onClick={fetchEmails}
                sx={{ textTransform: 'none', color: '#A78BFA', fontSize: '0.78rem' }}
              >
                Atualizar
              </Button>
            </Box>

            {loadingEmails ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <CircularProgress size={24} sx={{ color: '#7C3AED' }} />
              </Box>
            ) : emails.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Inbox size={32} color="#4B5563" />
                <Typography sx={{ color: '#4B5563', mt: 1, fontSize: '0.85rem' }}>
                  Nenhum email recebido ainda
                </Typography>
              </Box>
            ) : (
              <TableContainer sx={{ background: '#0D1117', borderRadius: 2, border: '1px solid rgba(255,255,255,0.06)' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: '#7C3AED', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>De</TableCell>
                      <TableCell sx={{ color: '#7C3AED', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>Assunto</TableCell>
                      <TableCell sx={{ color: '#7C3AED', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>Data</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {emails.map(e => (
                      <TableRow key={e._id} sx={{ '&:hover': { background: 'rgba(124,58,237,0.04)' } }}>
                        <TableCell sx={{ color: '#D1D5DB', fontSize: '0.82rem', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>{e.sender || e.from}</TableCell>
                        <TableCell sx={{ color: '#D1D5DB', fontSize: '0.82rem', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>{e.subject || '(sem assunto)'}</TableCell>
                        <TableCell sx={{ color: '#6B7280', fontSize: '0.78rem', borderBottom: '1px solid rgba(255,255,255,0.04)', whiteSpace: 'nowrap' }}>
                          {e.receivedAt ? new Date(e.receivedAt).toLocaleString('pt-BR') : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3, alignItems: 'center' }}>
              <Button startIcon={<ArrowLeft size={16} />} onClick={() => setActiveStep(0)}
                sx={{ textTransform: 'none', color: '#9CA3AF', '&:hover': { color: '#D1D5DB' } }}>Voltar ao início</Button>
              <Button
                variant="outlined" endIcon={<ExternalLink size={14} />}
                onClick={() => window.location.href = '/meus-emails'}
                sx={{ textTransform: 'none', borderColor: 'rgba(124,58,237,0.3)', color: '#A78BFA', '&:hover': { borderColor: '#7C3AED', background: 'rgba(124,58,237,0.08)' } }}
              >
                Ver todos os emails
              </Button>
            </Box>
          </SectionCard>
        )}
      </Box>
    </Fade>
  );
}
